import { createApp, nextTick } from 'vue';
import store from '../store';
import router from '../router';
import { registerMaterialSymbol } from './registerMaterialSymbol';
import { resolveVuetifyLocale } from '../utils/resolveVuetifyLocale.js';

/**
 * Page-level Vue islands. Each entry is loaded only when its custom element
 * is in the document. The Blade #app tree is not compiled or mounted.
 *
 * `needs` lists plugins that island actually uses. Europe map and the
 * payment-method logo grid do not receive the store.
 */
export const ISLAND_DEFINITIONS = [
    {
        selector: 'shopping-cart',
        aboveFold: true,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'shopping-cart' */ '../components/ShoppingCart.vue'),
    },
    {
        selector: 'buy-vignette',
        aboveFold: true,
        needs: { store: true, router: true, material: true },
        load: () => import(/* webpackChunkName: 'buy-vignette' */ '../components/BuyVignette/BuyVignette.vue'),
    },
    {
        selector: 'payment-processing',
        aboveFold: true,
        needs: { store: true, router: true, material: true },
        load: () => import(/* webpackChunkName: 'payment-processing' */ '../components/PaymentProcessing.vue'),
    },
    {
        selector: 'select-app-currency',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'select-app-currency' */ '../components/inputs/SelectAppCurrency.vue'),
    },
    {
        selector: 'select-app-language',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'select-app-language' */ '../components/inputs/SelectAppLanguage.vue'),
    },
    {
        selector: 'header-language-selector',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'header-language-selector' */ '../components/HeaderLanguageSelector.vue'),
    },
    {
        selector: 'mobile-header',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'mobile-header' */ '../components/MobileHeader.vue'),
    },
    {
        selector: 'pricing-section-hero2',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'pricing-section-hero2' */ '../components/PricingSectionHero2.vue'),
    },
    {
        selector: 'vignette-prices',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'vignette-prices' */ '../components/VignettePrices.vue'),
    },
    {
        selector: 'pricing-section',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'pricing-section' */ '../components/PricingSection.vue'),
    },
    {
        selector: 'map-popup',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'lazy-map-popup' */ '../components/LazyMapPopup.vue'),
    },
    {
        selector: 'home-guides-section',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'home-guides-section' */ '../components/HomeGuidesSection.vue'),
    },
    {
        selector: 'home-license-plate-search',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'home-license-plate-search' */ '../components/HomeLicensePlateSearch.vue'),
    },
    {
        selector: 'language-selector-block',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'language-selector-block' */ '../components/LanguageSelectorBlock.vue'),
    },
    {
        selector: 'blog-category-filters',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'blog-category-filters' */ '../components/BlogCategoryFilters.vue'),
    },
    {
        selector: 'contact-languages',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'contact-languages' */ '../components/ContactLanguages.vue'),
    },
    {
        selector: 'why-choose-us-language-dropdown',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'why-choose-us-language' */ '../components/WhyChooseUsLanguageDropdown.vue'),
    },
    {
        selector: 'payment-methods-pills',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'payment-methods-pills' */ '../components/PaymentMethodsPills.vue'),
    },
    {
        selector: 'payment-methods-country-select',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'payment-methods-country-select' */ '../components/PaymentMethodsCountrySelect.vue'),
    },
    {
        selector: 'payment-methods-grid',
        aboveFold: false,
        needs: {},
        load: () => import(/* webpackChunkName: 'payment-methods-grid' */ '../components/PaymentMethodsGrid.vue'),
    },
    {
        selector: 'payment-methods-ready-prices',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'payment-methods-ready-prices' */ '../components/PaymentMethodsReadyPrices.vue'),
    },
    {
        selector: 'how-it-works-validity-check-teaser',
        aboveFold: false,
        needs: { store: true, material: true },
        load: () => import(/* webpackChunkName: 'howitworks-validity-teaser' */ '../components/HowItWorksValidityCheckTeaser.vue'),
    },
    {
        selector: 'validity-check',
        aboveFold: true,
        needs: { store: true, router: true, material: true },
        load: () => import(/* webpackChunkName: 'validity-check' */ '../components/ValidityCheck.vue'),
    },
    {
        selector: 'europe-map',
        aboveFold: false,
        needs: { material: true },
        load: () => import(/* webpackChunkName: 'europe-map' */ '../components/EuropeMap.vue'),
    },
    {
        selector: 'team-4-flags',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'team4-flags' */ '../components/Team4Flags.vue'),
    },
    {
        selector: 'get-products',
        aboveFold: false,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'get-products' */ '../components/inputs/GetProducts.vue'),
    },
    {
        selector: 'cv404-buy-fields',
        aboveFold: true,
        needs: { store: true },
        load: () => import(/* webpackChunkName: 'cv404-buy-fields' */ '../components/Cv404BuyFields.vue'),
    },
];

const SKIP_DATA_ATTRIBUTES = new Set([
    'data-mount-when-near',
    'data-client-ready',
    'data-client-rendered',
    'data-geometry-ready',
    'data-ssr-fallback',
]);

const PLAIN_PROP_ATTRIBUTES = ['type', 'variant', 'active-type'];

const NEAR_VIEWPORT_MARGIN = '600px 0px';

let vuetifyLocaleWatchInstalled = false;

function kebabToCamel(value) {
    return value.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

function parseDataValue(raw) {
    if (raw == null) return undefined;
    const trimmed = String(raw).trim();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (trimmed === 'null') return null;
    if (trimmed === '') return '';
    const first = trimmed[0];
    if (first === '{' || first === '[' || first === '"') {
        try {
            return JSON.parse(trimmed);
        } catch (e) {
            return raw;
        }
    }
    return raw;
}

export function readIslandProps(el) {
    const props = {};
    for (const attr of Array.from(el.attributes)) {
        if (!attr.name.startsWith('data-') || SKIP_DATA_ATTRIBUTES.has(attr.name)) {
            continue;
        }
        const key = kebabToCamel(attr.name.slice(5));
        if (!key) continue;
        props[key] = parseDataValue(attr.value);
    }
    for (const name of PLAIN_PROP_ATTRIBUTES) {
        if (!el.hasAttribute(name)) continue;
        const key = kebabToCamel(name);
        if (props[key] !== undefined) continue;
        props[key] = el.getAttribute(name);
    }
    return props;
}

function syncVuetifyLocale(vuetify, resolveLocale) {
    if (!vuetify || vuetifyLocaleWatchInstalled) return;
    vuetifyLocaleWatchInstalled = true;
    const resolve = resolveLocale || resolveVuetifyLocale;
    const initialCode = typeof window !== 'undefined' ? window.__APP_DATA__?.lang?.code : null;
    vuetify.locale.current.value = resolve(initialCode);
    store.watch(
        (state) => state.lang?.code,
        (nextLangCode) => {
            vuetify.locale.current.value = resolve(nextLangCode);
        }
    );
}

function mountElement(el, definition, context) {
    const props = readIslandProps(el);
    definition.load()
        .then(async ({ default: Component }) => {
            if (!el.isConnected || el.__vue_app__) return;
            const app = createApp(Component, props);
            if (definition.needs.store) app.use(store);
            if (definition.needs.router) app.use(router);
            if (definition.needs.vuetify && context.vuetify) {
                app.use(context.vuetify);
                syncVuetifyLocale(context.vuetify, context.resolveVuetifyLocale);
            }
            if (definition.needs.material) registerMaterialSymbol(app);
            app.mount(el);
            await nextTick();
            if (el.isConnected) {
                el.setAttribute('data-client-rendered', 'true');
            }
        })
        .catch((err) => {
            el.__cvIslandScheduled = false;
            if (typeof console !== 'undefined' && console.error) {
                console.error(`[island] ${definition.selector} failed`, err);
            }
        });
}

function whenAppReady(run) {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (html.classList.contains('app-ready')) {
        run();
        return;
    }
    const observer = new MutationObserver(() => {
        if (!html.classList.contains('app-ready')) return;
        observer.disconnect();
        run();
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
}

function isClosedMobileDrawer(el) {
    return Boolean(el.closest('aside[role="dialog"]'));
}

function scheduleWhenDrawerOpen(el, definition, context) {
    const input = document.getElementById('cv-mobile-nav');
    if (!input) {
        scheduleWhenNear(el, definition, context);
        return;
    }
    const mount = () => {
        if (!input.checked) return;
        input.removeEventListener('change', mount);
        mountElement(el, definition, context);
    };
    if (input.checked) {
        mountElement(el, definition, context);
        return;
    }
    input.addEventListener('change', mount);
}

function isImmediateIsland(definition, el) {
    if (!definition.aboveFold) return false;
    if (definition.selector === 'select-app-currency') {
        return Boolean(el.closest('header')) && !isClosedMobileDrawer(el);
    }
    return true;
}

function scheduleWhenNear(el, definition, context) {
    // vignette-prices is `display:none` until hydrated; observe the SSR slot instead.
    const host = el.closest('.cv-vignette-prices-slot') || el.closest('.cv-ssr-pair') || el;
    if (typeof IntersectionObserver !== 'function') {
        mountElement(el, definition, context);
        return;
    }
    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        mountElement(el, definition, context);
    }, {
        root: null,
        rootMargin: NEAR_VIEWPORT_MARGIN,
        threshold: 0,
    });
    observer.observe(host);
}

/**
 * @param {{ vuetify?: object|null, resolveVuetifyLocale?: Function, mountWithoutVuetify?: boolean }} context
 * @returns {string[]} selectors scheduled during this call
 */
export function mountRegisteredIslands(context = {}) {
    const scheduled = [];
    if (typeof document === 'undefined') return scheduled;

    for (const definition of ISLAND_DEFINITIONS) {
        const nodes = document.querySelectorAll(definition.selector);
        if (!nodes.length) continue;

        const waitingForVuetify = definition.needs.vuetify
            && !context.vuetify
            && !context.mountWithoutVuetify;
        if (waitingForVuetify) continue;

        nodes.forEach((el) => {
            if (el.__cvIslandScheduled) return;
            el.__cvIslandScheduled = true;
            scheduled.push(definition.selector);
            if (definition.selector === 'select-app-currency' && isClosedMobileDrawer(el)) {
                scheduleWhenDrawerOpen(el, definition, context);
                return;
            }
            if (el.getAttribute('data-mount-when-near') === '1' || !isImmediateIsland(definition, el)) {
                scheduleWhenNear(el, definition, context);
                return;
            }
            whenAppReady(() => mountElement(el, definition, context));
        });
    }

    return scheduled;
}
